import React, { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "../../api/api";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import blogDeaultImage from "../../image/default blog.png";
import {
  ArrowRight,
  Clock,
  Eye,
  Heart,
  Calendar,
  User,
  BookOpen,
  ChevronDown,
} from "lucide-react";
const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

/* ─────────────────────────────────────────────
 READING PROGRESS (inside hero on scroll)
───────────────────────────────────────────── */
const HeroReadingBar = () => {
  const barRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(barRef.current, {
        scaleX: 1,
        ease: "none",
        scrollTrigger: {
          trigger: document.documentElement,
          start: "top top",
          end: "bottom bottom",
          scrub: 0.3,
        },
      });
    });
    return () => ctx.revert();
  }, []);

  return (
    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/5 z-30">
      <div
        ref={barRef}
        className="h-full origin-left"
        style={{
          background: "linear-gradient(90deg, #1d4ed8, #3b82f6, #f59e0b)",
          transform: "scaleX(0)",
        }}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────
 FLOATING META BADGE
───────────────────────────────────────────── */
const MetaBadge = ({ icon: Icon, value, color = "#3b82f6", delay = 0 }) => {
  const ref = useRef(null);

  useEffect(() => {
    gsap.to(ref.current, {
      y: -8,
      duration: 2.5 + delay,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
      delay,
    });
  }, [delay]);

  return (
    <div className="glass flex items-center gap-2 px-4 py-2 rounded-full border border-slate-600/50 bg-white/10">
      <Icon size={13} style={{ color }} />
      <span className="text-xs font-medium text-slate-300">{value}</span>
    </div>
  );
};

/* ─────────────────────────────────────────────
 SCROLL CUE
───────────────────────────────────────────── */
const ScrollCue = () => {
  const ref = useRef(null);

  useEffect(() => {
    gsap.to(ref.current, {
      y: 6,
      duration: 0.8,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
    });
  }, []);

  return (
    <div ref={ref} className="flex flex-col items-center gap-1 text-slate-600">
      <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
      <ChevronDown size={14} />
    </div>
  );
};

/* ─────────────────────────────────────────────
 MAIN BLOG HERO
───────────────────────────────────────────── */
const BlogHero = ({}) => {
  const sectionRef = useRef(null);
  const bgRef = useRef(null);
  const imgRef = useRef(null);
  const overlayRef = useRef(null);
  const titleRef = useRef(null);
  const excerptRef = useRef(null);
  const tagsRef = useRef(null);
  const ctaRef = useRef(null);
  const authorRef = useRef(null);
  const floatRef = useRef(null);
  const particlesRef = useRef([]);

  /* ─────────────────────────────────────────────
     FETCH DYNAMIC PUBLIC BLOG POOL
  ───────────────────────────────────────────── */
  const { data: blogResponse, isLoading } = useQuery({
    queryKey: ["public-blogs-hero"],
    queryFn: async () => {
      const response = await api.get("/api/blogs/public", {
        params: {
          is_featured: true,
        },
      });
      return response.data;
    },
  });

  const allPosts = blogResponse?.blogs || [];

  // Find the flagged item, or drop back seamlessly to the latest entry item pool
  const featured = allPosts.find((post) => post.is_featured) || allPosts[0];

  /* Entry animation (Runs exclusively once dynamic featured data has resolved safely) */
  useEffect(() => {
    if (isLoading || !featured) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.2 });

      /* BG image reveal */
      tl.fromTo(
        imgRef.current,
        { scale: 1.12, opacity: 0 },
        { scale: 1.04, opacity: 1, duration: 1.6, ease: "power2.out" },
      )
        .fromTo(
          overlayRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.8, ease: "power2.out" },
          "-=1.2",
        )
        /* Title lines */
        .fromTo(
          titleRef.current,
          { y: 60, opacity: 0, skewY: 2 },
          { y: 0, opacity: 1, skewY: 0, duration: 0.9, ease: "power4.out" },
          "-=0.6",
        )
        /* Excerpt */
        .fromTo(
          excerptRef.current,
          { y: 25, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
          "-=0.2",
        )
        /* Tags */
        .fromTo(
          tagsRef.current?.children ?? [],
          { y: 15, opacity: 0, scale: 0.9 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            stagger: 0.08,
            duration: 0.4,
            ease: "back.out(1.5)",
          },
          "-=0.1",
        )
        /* CTA */
        .fromTo(
          ctaRef.current?.children ?? [],
          { y: 20, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.1,
            duration: 0.5,
            ease: "power3.out",
          },
          "-=0.1",
        )
        /* Author card fade-in */
        .fromTo(
          authorRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
          "-=0.3",
        );

      /* BG Ken Burns loop */
      gsap.to(imgRef.current, {
        scale: 1.08,
        duration: 10,
        ease: "none",
        delay: 1.8,
      });

      /* Particles float */
      particlesRef.current.forEach((p, i) => {
        if (!p) return;
        gsap.to(p, {
          y: `${i % 2 === 0 ? "-" : ""}${12 + i * 4}`,
          x: `${i % 3 === 0 ? "-" : ""}${6 + i * 2}`,
          duration: 3 + i * 0.8,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
          delay: i * 0.3,
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [isLoading, featured]);

  /* Scroll parallax interaction setup */
  useEffect(() => {
    if (isLoading || !featured) return;

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top top",
        end: "bottom top",
        scrub: true,
        onUpdate: (self) => {
          const p = self.progress;
          if (imgRef.current) gsap.set(imgRef.current, { y: p * 100 });
          if (bgRef.current) gsap.set(bgRef.current, { y: p * 60 });
          if (overlayRef.current)
            gsap.set(overlayRef.current, {
              opacity: Math.min(1, 0.7 + p * 0.3),
            });
        },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, [isLoading, featured]);

  if (isLoading) {
    return (
      <div
        className="w-full bg-[#060d24] flex items-center justify-center"
        style={{ height: "100dvh", minHeight: "650px" }}
      >
        <div className="w-12 h-12 border-2 border-primary-500/20 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!featured) return null;

  const particles = [
    {
      top: "20%",
      left: "60%",
      size: 120,
      color: "rgba(29,78,216,0.15)",
      blur: 40,
    },
    {
      top: "65%",
      left: "75%",
      size: 80,
      color: "rgba(245,158,11,0.1)",
      blur: 30,
    },
    {
      top: "40%",
      left: "85%",
      size: 60,
      color: "rgba(99,102,241,0.12)",
      blur: 25,
    },
    {
      top: "75%",
      left: "15%",
      size: 100,
      color: "rgba(29,78,216,0.08)",
      blur: 35,
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-[#060d24]"
      style={{ height: "100dvh", minHeight: "650px" }}
    >
      {/* ── BG IMAGE ── */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <img
          ref={imgRef}
          src={
            featured.image ? `${backendUrl}${featured.image}` : blogDeaultImage
          }
          alt={featured.title}
          className="w-full h-full object-cover will-change-transform"
          style={{ opacity: 0, transform: "scale(1.12)" }}
        />
      </div>

      {/* ── GRADIENT OVERLAY ── */}
      <div
        ref={overlayRef}
        className="absolute inset-0 z-[1]"
        style={{
          background: `
            linear-gradient(to right, rgba(6,13,36,0.96) 0%, rgba(6,13,36,0.7) 45%, rgba(6,13,36,0.3) 100%),
            linear-gradient(to top, rgba(6,13,36,0.96) 0%, transparent 40%),
            linear-gradient(to bottom, rgba(6,13,36,0.5) 0%, transparent 30%)
          `,
          opacity: 0,
        }}
      />

      {/* ── BG CONTENT LAYER ── */}
      <div ref={bgRef} className="absolute inset-0 z-[2] pointer-events-none">
        <div className="absolute inset-0 dot-pattern opacity-15" />
        {particles.map((p, i) => (
          <div
            key={i}
            ref={(el) => (particlesRef.current[i] = el)}
            className="absolute rounded-full"
            style={{
              top: p.top,
              left: p.left,
              width: p.size,
              height: p.size,
              background: p.color,
              filter: `blur(${p.blur}px)`,
            }}
          />
        ))}
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="absolute inset-0 z-10 flex flex-col justify-center px-6 sm:px-14 lg:px-20 pt-24 pb-32">
        <div className="max-w-2xl xl:max-w-3xl space-y-6">
          {/* Title */}
          <div className="overflow-hidden">
            <h1
              ref={titleRef}
              className="font-display font-black text-white leading-tight"
              style={{
                fontSize: "clamp(1.8rem, 4.5vw, 3.5rem)",
                opacity: 0,
              }}
            >
              {featured.title}
            </h1>
          </div>

          {/* Excerpt */}
          <p
            ref={excerptRef}
            className="text-slate-300 leading-relaxed max-w-xl"
            style={{
              fontSize: "clamp(0.9rem, 1.4vw, 1.05rem)",
              opacity: 0,
            }}
          >
            {featured.excerpt}
          </p>

          {/* Tags */}
          <div ref={tagsRef} className="flex flex-wrap gap-2">
            {featured.tags &&
              featured.tags.map((tag, i) => (
                <span
                  key={i}
                  className="tag text-xs cursor-pointer text-slate-300 border border-slate-700/50 bg-white/5 px-3 py-1 rounded-full hover:bg-primary-600/30 transition-colors duration-300"
                  style={{ opacity: 0 }}
                >
                  {tag}
                </span>
              ))}
          </div>

          {/* CTA */}
          <div ref={ctaRef} className="flex flex-wrap items-center gap-4">
            <Link
              to={featured.href}
              className="group relative inline-flex items-center gap-2.5 px-7 py-3.5 bg-primary-600 hover:bg-primary-500 text-white font-semibold rounded-xl text-sm overflow-hidden transition-all duration-300 hover:shadow-glow-primary hover:-translate-y-0.5"
              style={{ opacity: 0 }}
            >
              <span className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/15 to-transparent" />
              <BookOpen size={15} />
              <span>Read Full Article</span>
              <ArrowRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>

            <div
              className="flex items-center gap-4 text-xs text-slate-400"
              style={{ opacity: 0 }}
            >
              <span className="flex items-center gap-1.5">
                <Clock size={12} className="text-primary-400" />
                {featured.readTime}
              </span>
              <span className="w-px h-4 bg-white/10" />
              <span className="flex items-center gap-1.5">
                <Eye size={12} className="text-secondary-400" />
                {featured.views} views
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── AUTHOR CARD ── */}
      <div
        ref={authorRef}
        className="absolute bottom-24 right-6 sm:right-14 lg:right-20 z-20 hidden sm:flex items-center gap-3 glass rounded-2xl px-4 py-3 border border-slate-600/50 bg-white/10"
        style={{ opacity: 0 }}
      >
        {featured.author?.avatar ? (
          <img
            src={featured.author.avatar}
            alt={featured.author.name}
            className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
          />
        ) : (
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-slate-800 text-slate-400 border border-slate-700">
            <User size={18} />
          </div>
        )}
        <div>
          <p className="text-xs font-bold text-white">
            {featured.author?.name}
          </p>
          <p className="text-[10px] text-slate-400">{featured.author?.role}</p>
        </div>
        <div className="w-px h-8 bg-slate-600" />
        <div className="text-right">
          <p className="text-[10px] text-slate-500">Published</p>
          <p className="text-xs text-secondary-600 font-medium">
            {featured.date}
          </p>
        </div>
      </div>

      {/* ── FLOATING META BADGES ── */}
      <div
        ref={floatRef}
        className="absolute right-6 top-1/2 -translate-y-1/2 z-20 hidden xl:flex flex-col gap-3"
      >
        <MetaBadge
          icon={Eye}
          value={`${featured.views} reads`}
          color="#3b82f6"
          delay={0}
        />
        <MetaBadge
          icon={Heart}
          value={`${featured.likes || 0} likes`}
          color="#f59e0b"
          delay={0.3}
        />
        <MetaBadge
          icon={Clock}
          value={featured.readTime}
          color="#6366f1"
          delay={0.6}
        />
      </div>

      {/* ── BOTTOM SCROLL CUE ── */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
        <ScrollCue />
      </div>

      {/* ── READING PROGRESS BAR ── */}
      <HeroReadingBar />

      {/* ── CORNER DECORATIONS ── */}
      <div className="absolute top-28 left-6 z-20 hidden sm:block">
        <div className="w-10 h-10 border-l-2 border-t-2 border-primary-600/30 rounded-tl-xl" />
      </div>
      <div className="absolute bottom-28 right-6 z-20 hidden sm:block">
        <div className="w-10 h-10 border-r-2 border-b-2 border-secondary-400/30 rounded-br-xl" />
      </div>
    </section>
  );
};

export default BlogHero;
